import { ElementType, FC, PropsWithChildren, ReactNode } from 'react';
import { css, Interpolation, Theme } from '@emotion/react';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';

interface GlassCardProps {
  containerStyles?: Interpolation<Theme>;
  as?: ElementType;
}

interface GlassCardNamespace extends FC<PropsWithChildren<GlassCardProps>> {
  Header: typeof Header;
  Body: typeof Body;
}

export const GlassCard: GlassCardNamespace = ({ children, containerStyles, as: Component = 'div' }) => {
  return <Component css={[styles.container, containerStyles]}>{children}</Component>;
};

interface GlassCardHeaderProps {
  headerStyles?: Interpolation<Theme>;
  icon?: ReactNode;
  title?: ReactNode;
  subTitle?: ReactNode;
}
export const Header: FC<PropsWithChildren<GlassCardHeaderProps>> = ({ children, headerStyles, title, subTitle }) => {
  return (
    <div css={[styles.header, headerStyles]}>
      {typeof title === 'string' ? (
        <Text as='h4' size='large' weight='bold'>
          {title}
        </Text>
      ) : (
        title
      )}
      {typeof subTitle === 'string' ? (
        <Text size='small' css={{ color: theme.colors.neutral50 }}>
          {subTitle}
        </Text>
      ) : (
        subTitle
      )}
      {children}
    </div>
  );
};

interface GlassCardBodyProps {
  bodyStyles?: Interpolation<Theme>;
  as?: ElementType;
}
export const Body: FC<PropsWithChildren<GlassCardBodyProps>> = ({ bodyStyles, children, as: Component = 'div' }) => {
  return <Component css={[styles.body, bodyStyles]}>{children}</Component>;
};

GlassCard.Header = Header;
GlassCard.Body = Body;

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
    border-radius: ${theme.borderRadius.medium};
    border: 2px solid ${theme.colors.white}80;
    padding: ${theme.spacing(2)};
    backdrop-filter: blur(4px);
    background-color: ${theme.colors.white}80;
    box-shadow: 0px 3px 8px 1px ${theme.colors.black}26;
  `,
  header: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(2)};
  `,
  body: css`
    display: flex;
    flex-direction: column;
    background-color: ${theme.colors.white};
    border-radius: ${theme.borderRadius.medium};
  `,
};
